import {
  COMMUNITY_INTERACTION_MEDIA_TYPES,
  COMMUNITY_INTERACTION_TYPES,
  COMMUNITY_AD_ENABLED_TYPES,
} from '@/app/community/constants';
import { getUserInitials, getFormattedUserName } from '@/utils/user';
import { decodeSpansFromText, getSpanText } from '@/utils/commenting';
import dayjs from 'dayjs';

export const portraitMediaTypes = Object.freeze([
  COMMUNITY_INTERACTION_MEDIA_TYPES.REELS,
  COMMUNITY_INTERACTION_MEDIA_TYPES.SHORTS,
]);

const previewPresetTexts = Object.freeze({
  attachment: 'with media',
});

const commentAuthorNameField = Object.freeze({
  INSTAGRAM: 'instagramUsername',
  FACEBOOK: 'facebookUsername',
  TIKTOK: 'tiktokUsername',
  TWITTER: 'authorUsername',
  YOUTUBE: 'authorUsername',
});

const conversationAuthorField = Object.freeze({
  [COMMUNITY_INTERACTION_TYPES.INSTAGRAM_CONVERSATION]: 'followerParticipant',
  [COMMUNITY_INTERACTION_TYPES.FACEBOOK_CONVERSATION]: 'fbFollowerParticipant',
  [COMMUNITY_INTERACTION_TYPES.TWITTER_CONVERSATION]: 'twitterFollowerParticipant',
});

function customInteractionData(interaction) {
  let data = {};
  // Conversation type
  if (interaction?.type?.includes('CONVERSATION')) {
    const { searchResultSnippet, mostRecentMessage } = interaction;
    const author = interaction[conversationAuthorField[interaction.type]];
    data = {
      authorName: author?.username,
      avatar: { image: author?.profilePictureUrl },
      text: searchResultSnippet ?? mostRecentMessage,
    };
  }
  // Comment type
  else {
    const isPortraitMedia =
      interaction.type.includes('TIKTOK') || portraitMediaTypes.includes(interaction.mediaType);
    const channel = interaction.type.split('_')[0];
    data = {
      authorName: interaction[commentAuthorNameField[channel]],
    };

    // Non-UGC thumbnail
    if (
      interaction.mediaId ||
      interaction.type === COMMUNITY_INTERACTION_TYPES.TIKTOK_COMMENT_MENTION
    ) {
      data.media = { src: interaction.postThumbnailUrl ?? interaction.thumbnailUrl };
      if (isPortraitMedia) {
        data.media.aspect = 'portrait';
      }
    }
    // UGC Tweet thumbnail
    else if (channel === 'TWITTER') {
      data.avatar = { image: interaction.authorProfilePictureUrl };
    }
    // Attachment preview text
    if (
      (!!interaction?.attachment?.src || interaction?.attachments?.length > 0) &&
      interaction.type !== COMMUNITY_INTERACTION_TYPES.TWITTER_RETWEET
    ) {
      data.text = previewPresetTexts.attachment;
    }
  }

  if (Object.values(previewPresetTexts).includes(data.text)) {
    data.textClass = 'dash-italic';
  }
  return { ...interaction, ...data };
}

function sharedInteractionData(interaction) {
  const {
    id,
    type,
    text,
    sourceUpdatedAt,
    searchResultSnippet,
    isFollowUp,
    isArchived,
    isRead,
    tagIds,
    assigneeId,
    isAdComment,
    isAuthorBlocked,
    isHidden,
    deletedAt,
  } = interaction ?? {};

  return {
    id,
    type,
    text: searchResultSnippet ?? text,
    timestamp: sourceUpdatedAt,
    isFollowUp,
    isArchived,
    isRead,
    tagIds,
    assigneeId,
    isAdComment: isAdComment && COMMUNITY_AD_ENABLED_TYPES.includes(type),
    isAuthorBlocked,
    isHidden,
    isDeleted: !!deletedAt,
  };
}

export function getFormattedInteraction(interaction) {
  return {
    ...sharedInteractionData(interaction),
    ...customInteractionData(interaction),
  };
}

export function getFormattedInternalNote(internalNote) {
  const { id, createdAt, text, authorUser, type, isRead, isAdComment, users } = internalNote;

  let internalNoteText = text;
  if (text) {
    const textSpans = decodeSpansFromText(text, users);
    internalNoteText = textSpans.map((span) => getSpanText(span)).join('');
  }

  return {
    id,
    timestamp: createdAt,
    text: internalNoteText,
    authorName: getFormattedUserName(authorUser),
    avatar: authorUser?.avatarUrl
      ? { image: authorUser.avatarUrl }
      : { label: getUserInitials(authorUser) },
    type,
    isRead,
    isAdComment,
  };
}

export function getTimestampString(timestamp, timezone) {
  const datetime = dayjs(timestamp);
  if (datetime) {
    const now = timezone ? dayjs().tz(timezone) : dayjs().local();
    const tzConverted = timezone ? datetime.tz(timezone) : datetime.local();

    let timestampFormat = now.isSame(tzConverted, 'days') ? 'h:mm A' : 'MMM D';
    if (!now.isSame(tzConverted, 'year')) {
      timestampFormat = 'MMM D, YYYY';
    }
    return tzConverted.format(timestampFormat);
  }
  return '';
}
